import { useCallback } from "react";
import { JournalEntrySourceType } from "@common/schemas/domains/accounting/journalEntry";
import { JournalEntrySource } from "@common/types/domains/accounting/journalEntry";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useOverlays } from "contexts/OverlayContext";
import {
    defaultGlTransactionsDrilldown,
    GlTransactionsDrilldownConfig,
} from "./glTransactionsDrilldown";

interface UseGlSourceDrilldownParams {
    basis: AccountingLedgerBasis;
    transactionsDrilldown?: GlTransactionsDrilldownConfig;
}

interface GlSourceLine {
    entityId: number;
    source: JournalEntrySource;
}

export function useGlSourceDrilldown({
    basis,
    transactionsDrilldown = defaultGlTransactionsDrilldown,
}: UseGlSourceDrilldownParams): (line: GlSourceLine) => void {
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const { openOverlay } = useOverlays();
    const isClassesEnabled = useIsClassesEnabled();

    return useCallback(
        ({ entityId, source }: GlSourceLine) => {
            const entityIds = [entityId];
            if (source.type === JournalEntrySourceType.Transaction) {
                openGlobalTransactionViewDrawer(
                    { transactionIds: [Number(source.id)], entityIds },
                    {
                        cacheKey: transactionsDrilldown.getCacheKey(basis),
                        baseViewOverride: transactionsDrilldown.createView(
                            basis,
                            { isClassesEnabled },
                        ),
                        shouldSuggestSimilarTransactionsUpdate: true,
                    },
                );
            } else if (source.type === JournalEntrySourceType.Bill) {
                openOverlay("billLineItemsDrilldown", {
                    billId: String(source.id),
                    entityIds,
                });
            } else if (source.type === JournalEntrySourceType.Invoice) {
                openOverlay("invoiceLineItemsDrilldown", {
                    invoiceId: String(source.id),
                    entityIds,
                });
            }
        },
        [
            basis,
            isClassesEnabled,
            openGlobalTransactionViewDrawer,
            openOverlay,
            transactionsDrilldown,
        ],
    );
}
